import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  buscarProduto,
  salvarMeta,
  adicionarAtributo,
  removerAtributo,
  uploadImagem,
  atualizarImagem,
  removerImagem,
  reordenarImagens,
  gerarDescricaoIA,
  type MetaInput,
} from './api'

export default function ProdutoDetalhe() {
  const { codigo = '' } = useParams()
  const qc = useQueryClient()
  const { data, isLoading, error } = useQuery({
    queryKey: ['produto', codigo],
    queryFn: () => buscarProduto(codigo),
    enabled: !!codigo,
  })

  if (isLoading) return <p className="p-8 text-slate-500">Carregando…</p>
  if (error)
    return (
      <p className="p-8 text-red-600">
        Erro: {error instanceof Error ? error.message : String(error)}
      </p>
    )
  if (!data) return null

  return (
    <div className="max-w-[1100px] mx-auto px-4 py-6 space-y-6">
      <div>
        <Link to="/" className="text-sm text-blue-700 hover:underline">
          ← Voltar para a lista
        </Link>
        <h1 className="text-2xl font-bold text-slate-900 mt-1">{data.produto.nome}</h1>
        <p className="text-sm text-slate-500 font-mono">
          SKU {codigo} · R$ {data.produto.preco_venda} · estoque {data.produto.estoque_atual}
          {data.produto.departamento && ` · ${data.produto.departamento}`}
          {data.produto.categoria_nome && ` / ${data.produto.categoria_nome}`}
        </p>
      </div>

      <ErpReadonly data={data} />
      <MetaForm
        codigo={codigo}
        atual={data}
        onSaved={() => qc.invalidateQueries({ queryKey: ['produto', codigo] })}
      />
      <AtributosBloco
        codigo={codigo}
        atributos={data.atributos}
        onChange={() => qc.invalidateQueries({ queryKey: ['produto', codigo] })}
      />
      <ImagensBloco
        codigo={codigo}
        imagens={data.imagens}
        onChange={() => qc.invalidateQueries({ queryKey: ['produto', codigo] })}
      />
    </div>
  )
}

function ErpReadonly({ data }: { data: ProdutoDetalheData }) {
  return (
    <section className="bg-white border border-slate-200 rounded-xl p-5">
      <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-3">
        Dados do ERP (somente leitura)
      </h2>
      <dl className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
        <Field label="Marca ERP" value={data.produto.marca || '—'} />
        <Field label="Grupo ERP" value={data.produto.grupo || '—'} />
        <Field label="Peso ERP" value={data.produto.peso ? `${data.produto.peso} kg` : '—'} />
        <Field label="Atualizado em" value={new Date(data.produto.atualizado_em).toLocaleString('pt-BR')} />
      </dl>
      {data.produto.descricao && (
        <div className="mt-4">
          <p className="text-xs uppercase tracking-wide text-slate-500 mb-1">Descrição ERP</p>
          <p className="text-sm text-slate-700">{data.produto.descricao}</p>
        </div>
      )}
    </section>
  )
}

function MetaForm({
  codigo,
  atual,
  onSaved,
}: {
  codigo: string
  atual: ProdutoDetalheData
  onSaved: () => void
}) {
  const [form, setForm] = useState<MetaInput>({
    descricao_longa: atual.produto.descricao_longa,
    bullets: atual.produto.bullets ?? [],
    marca_override: atual.produto.marca_override,
    peso: atual.produto.peso_override ? Number(atual.produto.peso_override) : null,
    altura: atual.produto.altura,
    largura: atual.produto.largura,
    comprimento: atual.produto.comprimento,
  })

  useEffect(() => {
    setForm({
      descricao_longa: atual.produto.descricao_longa,
      bullets: atual.produto.bullets ?? [],
      marca_override: atual.produto.marca_override,
      peso: atual.produto.peso_override ? Number(atual.produto.peso_override) : null,
      altura: atual.produto.altura,
      largura: atual.produto.largura,
      comprimento: atual.produto.comprimento,
    })
  }, [atual])

  const mut = useMutation({
    mutationFn: () =>
      salvarMeta(codigo, {
        ...form,
        bullets: form.bullets && form.bullets.length > 0 ? form.bullets.filter(Boolean) : null,
      }),
    onSuccess: onSaved,
  })

  const ia = useMutation({
    mutationFn: () => gerarDescricaoIA(codigo),
    onSuccess: (r) => {
      // Substitui descrição e bullets atuais pelos sugeridos.
      // O usuário pode editar antes de salvar.
      setForm((f) => ({
        ...f,
        descricao_longa: r.descricao,
        bullets: r.bullets,
      }))
    },
  })

  return (
    <section className="bg-white border border-slate-200 rounded-xl p-5">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
          Enriquecimento (descrição rica, marca, dimensões)
        </h2>
        <div className="flex items-center gap-3">
          {ia.isSuccess && (
            <span className="text-xs text-blue-600">
              ✦ IA gerou (revise antes de salvar)
            </span>
          )}
          {ia.isError && (
            <span className="text-xs text-red-600">
              IA: {String((ia.error as Error).message)}
            </span>
          )}
          <button
            type="button"
            onClick={() => ia.mutate()}
            disabled={ia.isPending}
            className="text-xs bg-purple-100 hover:bg-purple-200 text-purple-800 font-medium px-3 py-1 rounded-full transition disabled:opacity-50"
          >
            {ia.isPending ? 'gerando…' : '✦ Gerar com IA'}
          </button>
          {mut.isSuccess && <span className="text-xs text-green-600">✓ salvo</span>}
          {mut.isError && (
            <span className="text-xs text-red-600">erro: {String((mut.error as Error).message)}</span>
          )}
        </div>
      </div>

      <div className="space-y-4">
        <Textarea
          label="Descrição rica (markdown)"
          rows={6}
          value={form.descricao_longa ?? ''}
          onChange={(v) => setForm((f) => ({ ...f, descricao_longa: v || null }))}
          placeholder="Texto longo que aparece na página do produto. Aceita markdown."
        />

        <BulletsEditor
          bullets={form.bullets ?? []}
          onChange={(b) => setForm((f) => ({ ...f, bullets: b }))}
        />

        <Input
          label="Marca (override do ERP — só preencha se ERP vier vazio/errado)"
          value={form.marca_override ?? ''}
          onChange={(v) => setForm((f) => ({ ...f, marca_override: v || null }))}
        />

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <NumberInput
            label="Peso (kg)"
            value={form.peso}
            onChange={(v) => setForm((f) => ({ ...f, peso: v }))}
          />
          <NumberInput
            label="Altura (cm)"
            value={form.altura}
            onChange={(v) => setForm((f) => ({ ...f, altura: v }))}
          />
          <NumberInput
            label="Largura (cm)"
            value={form.largura}
            onChange={(v) => setForm((f) => ({ ...f, largura: v }))}
          />
          <NumberInput
            label="Comprimento (cm)"
            value={form.comprimento}
            onChange={(v) => setForm((f) => ({ ...f, comprimento: v }))}
          />
        </div>

        <div className="flex justify-end pt-2 border-t border-slate-100">
          <button
            onClick={() => mut.mutate()}
            disabled={mut.isPending}
            className="bg-blue-700 hover:bg-blue-800 text-white px-5 py-2 rounded-lg text-sm font-semibold disabled:opacity-50"
          >
            {mut.isPending ? 'Salvando…' : 'Salvar enriquecimento'}
          </button>
        </div>
      </div>
    </section>
  )
}

function BulletsEditor({
  bullets,
  onChange,
}: {
  bullets: string[]
  onChange: (b: string[]) => void
}) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-wide text-slate-500 mb-2">
        Bullets (3-5 frases curtas de benefícios)
      </label>
      <div className="space-y-2">
        {bullets.map((b, i) => (
          <div key={i} className="flex gap-2">
            <input
              type="text"
              value={b}
              onChange={(e) => {
                const novo = [...bullets]
                novo[i] = e.target.value
                onChange(novo)
              }}
              className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm"
              placeholder={`Bullet ${i + 1}`}
            />
            <button
              type="button"
              onClick={() => onChange(bullets.filter((_, j) => j !== i))}
              className="px-3 text-slate-400 hover:text-red-600"
              aria-label="Remover"
            >
              ✕
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={() => onChange([...bullets, ''])}
          className="text-xs text-blue-700 hover:underline"
        >
          + Adicionar bullet
        </button>
      </div>
    </div>
  )
}

function AtributosBloco({
  codigo,
  atributos,
  onChange,
}: {
  codigo: string
  atributos: Array<{ id: number; chave: string; valor: unknown; ordem: number }>
  onChange: () => void
}) {
  const [chave, setChave] = useState('')
  const [valor, setValor] = useState('')

  const add = useMutation({
    mutationFn: () => adicionarAtributo(codigo, chave.trim(), valor),
    onSuccess: () => {
      setChave('')
      setValor('')
      onChange()
    },
  })
  const del = useMutation({
    mutationFn: (id: number) => removerAtributo(id),
    onSuccess: onChange,
  })

  return (
    <section className="bg-white border border-slate-200 rounded-xl p-5">
      <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-3">
        Atributos / Ficha técnica
      </h2>

      {atributos.length > 0 ? (
        <table className="w-full text-sm mb-4">
          <thead className="text-xs text-slate-500 uppercase">
            <tr>
              <th className="text-left py-1">Chave</th>
              <th className="text-left py-1">Valor</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {atributos.map((a) => (
              <tr key={a.id} className="border-t border-slate-100">
                <td className="py-1.5 pr-4 font-mono text-xs text-slate-700">{a.chave}</td>
                <td className="py-1.5 text-slate-800">
                  {typeof a.valor === 'string' ? a.valor : JSON.stringify(a.valor)}
                </td>
                <td className="py-1.5 text-right">
                  <button
                    onClick={() => del.mutate(a.id)}
                    className="text-slate-400 hover:text-red-600 text-xs"
                  >
                    remover
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="text-sm text-slate-400 mb-4">Nenhum atributo cadastrado.</p>
      )}

      <div className="flex gap-2 items-end">
        <div className="flex-1">
          <label className="block text-xs uppercase tracking-wide text-slate-500 mb-1">Chave</label>
          <input
            type="text"
            value={chave}
            onChange={(e) => setChave(e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
            placeholder="ex: voltagem, principio_ativo"
          />
        </div>
        <div className="flex-1">
          <label className="block text-xs uppercase tracking-wide text-slate-500 mb-1">Valor</label>
          <input
            type="text"
            value={valor}
            onChange={(e) => setValor(e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
            placeholder="ex: 110V, Ivermectina 1%"
          />
        </div>
        <button
          onClick={() => add.mutate()}
          disabled={add.isPending || !chave.trim()}
          className="bg-slate-700 hover:bg-slate-800 text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50"
        >
          + Adicionar
        </button>
      </div>
      {add.isError && (
        <p className="text-xs text-red-600 mt-2">erro: {String((add.error as Error).message)}</p>
      )}
    </section>
  )
}

type ImagemRow = ProdutoDetalheData['imagens'][number]

const CANAIS_DISPONIVEIS = ['todos', 'web', 'ml', 'shopee', 'whatsapp']
const TIPOS_DISPONIVEIS = ['frontal', 'embalagem', 'lifestyle', 'detalhe', 'ficha_tecnica']

function ImagensBloco({
  codigo,
  imagens,
  onChange,
}: {
  codigo: string
  imagens: ImagemRow[]
  onChange: () => void
}) {
  const [erro, setErro] = useState<string | null>(null)
  const [enviando, setEnviando] = useState(false)
  const [arrastando, setArrastando] = useState(false)

  const upload = useMutation({
    mutationFn: async (arquivos: FileList) => {
      setErro(null)
      setEnviando(true)
      try {
        for (const f of Array.from(arquivos)) {
          await uploadImagem(codigo, f, { canais: ['todos'] })
        }
      } finally {
        setEnviando(false)
      }
    },
    onSuccess: onChange,
    onError: (e) => setErro(e instanceof Error ? e.message : String(e)),
  })

  const handleFiles = (files: FileList | null) => {
    if (!files || files.length === 0) return
    upload.mutate(files)
  }

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setArrastando(false)
    handleFiles(e.dataTransfer.files)
  }

  return (
    <section className="bg-white border border-slate-200 rounded-xl p-5">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
          Imagens ({imagens.length})
        </h2>
        {erro && <span className="text-xs text-red-600">erro: {erro}</span>}
      </div>

      <label
        onDragOver={(e) => {
          e.preventDefault()
          setArrastando(true)
        }}
        onDragLeave={() => setArrastando(false)}
        onDrop={onDrop}
        className={`block border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition ${
          arrastando ? 'border-blue-500 bg-blue-50' : 'border-slate-300 hover:border-slate-400'
        }`}
      >
        <input
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(e) => handleFiles(e.target.files)}
          disabled={enviando}
        />
        <p className="text-sm text-slate-600">
          {enviando ? (
            'Enviando…'
          ) : (
            <>
              <span className="font-medium text-blue-700">Clique para enviar</span> ou arraste imagens aqui
            </>
          )}
        </p>
        <p className="text-xs text-slate-400 mt-1">JPG, PNG, WEBP — máx. 20MB cada</p>
      </label>

      {imagens.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mt-5">
          {imagens.map((img, idx) => (
            <ImagemCard
              key={img.id}
              imagem={img}
              codigo={codigo}
              total={imagens.length}
              indice={idx}
              ids={imagens.map((i) => i.id)}
              onChange={onChange}
            />
          ))}
        </div>
      )}
    </section>
  )
}

function ImagemCard({
  imagem,
  codigo,
  total,
  indice,
  ids,
  onChange,
}: {
  imagem: ImagemRow
  codigo: string
  total: number
  indice: number
  ids: number[]
  onChange: () => void
}) {
  const [editando, setEditando] = useState(false)

  const save = useMutation({
    mutationFn: (patch: Parameters<typeof atualizarImagem>[1]) => atualizarImagem(imagem.id, patch),
    onSuccess: () => {
      setEditando(false)
      onChange()
    },
  })

  const del = useMutation({
    mutationFn: () => removerImagem(imagem.id),
    onSuccess: onChange,
  })

  const move = useMutation({
    mutationFn: (direcao: -1 | 1) => {
      const novos = [...ids]
      const j = indice + direcao
      if (j < 0 || j >= novos.length) return Promise.resolve()
      ;[novos[indice], novos[j]] = [novos[j]!, novos[indice]!]
      return reordenarImagens(codigo, novos)
    },
    onSuccess: onChange,
  })

  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden bg-white">
      <div className="aspect-square bg-slate-100 relative">
        <img src={imagem.url} alt={imagem.alt ?? ''} className="w-full h-full object-cover" />
        <span className="absolute top-1.5 left-1.5 bg-slate-900/70 text-white text-[10px] px-1.5 py-0.5 rounded">
          {imagem.ordem}
        </span>
      </div>

      <div className="p-2.5 text-xs space-y-1.5">
        <div className="flex flex-wrap gap-1">
          {imagem.canais.map((c) => (
            <span
              key={c}
              className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                c === 'todos' ? 'bg-blue-100 text-blue-800' : 'bg-slate-100 text-slate-700'
              }`}
            >
              {c}
            </span>
          ))}
          {imagem.tipo && (
            <span className="px-1.5 py-0.5 rounded text-[10px] bg-amber-100 text-amber-800">
              {imagem.tipo}
            </span>
          )}
        </div>

        <div className="flex justify-between text-[11px] text-slate-500">
          <span>
            {imagem.largura}×{imagem.altura}
          </span>
          <div className="flex gap-1">
            <button
              onClick={() => move.mutate(-1)}
              disabled={indice === 0 || move.isPending}
              className="text-slate-400 hover:text-slate-700 disabled:opacity-30"
              aria-label="Mover para cima"
            >
              ↑
            </button>
            <button
              onClick={() => move.mutate(1)}
              disabled={indice === total - 1 || move.isPending}
              className="text-slate-400 hover:text-slate-700 disabled:opacity-30"
              aria-label="Mover para baixo"
            >
              ↓
            </button>
            <button
              onClick={() => setEditando((e) => !e)}
              className="text-blue-600 hover:underline"
            >
              {editando ? 'Cancelar' : 'Editar'}
            </button>
            <button
              onClick={() => {
                if (confirm('Remover essa imagem?')) del.mutate()
              }}
              className="text-red-600 hover:underline"
            >
              Remover
            </button>
          </div>
        </div>

        {editando && (
          <ImagemEditor imagem={imagem} onSave={(p) => save.mutate(p)} disabled={save.isPending} />
        )}
      </div>
    </div>
  )
}

function ImagemEditor({
  imagem,
  onSave,
  disabled,
}: {
  imagem: ImagemRow
  onSave: (p: { canais: string[]; tipo: string | null; alt: string | null }) => void
  disabled: boolean
}) {
  const [canais, setCanais] = useState<string[]>(imagem.canais)
  const [tipo, setTipo] = useState<string | null>(imagem.tipo)
  const [alt, setAlt] = useState<string>(imagem.alt ?? '')

  const toggleCanal = (c: string) => {
    if (canais.includes(c)) {
      setCanais(canais.filter((x) => x !== c))
    } else {
      // Marcar 'todos' desmarca os outros, e vice-versa
      const novos = c === 'todos' ? ['todos'] : [...canais.filter((x) => x !== 'todos'), c]
      setCanais(novos)
    }
  }

  return (
    <div className="space-y-2 pt-2 border-t border-slate-100">
      <div>
        <p className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">Canais</p>
        <div className="flex flex-wrap gap-1">
          {CANAIS_DISPONIVEIS.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => toggleCanal(c)}
              className={`px-2 py-0.5 rounded text-[10px] border ${
                canais.includes(c)
                  ? 'bg-blue-700 text-white border-blue-700'
                  : 'bg-white text-slate-600 border-slate-300 hover:border-slate-400'
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      </div>
      <div>
        <p className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">Tipo</p>
        <select
          value={tipo ?? ''}
          onChange={(e) => setTipo(e.target.value || null)}
          className="w-full px-2 py-1 border border-slate-300 rounded text-xs"
        >
          <option value="">— sem tipo —</option>
          {TIPOS_DISPONIVEIS.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
      </div>
      <div>
        <p className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">
          Texto alternativo (acessibilidade)
        </p>
        <input
          type="text"
          value={alt}
          onChange={(e) => setAlt(e.target.value)}
          placeholder="Ex: Frasco de Bimectin 50ml visto de frente"
          className="w-full px-2 py-1 border border-slate-300 rounded text-xs"
        />
      </div>
      <button
        onClick={() => onSave({ canais, tipo, alt: alt.trim() || null })}
        disabled={disabled}
        className="w-full bg-blue-700 hover:bg-blue-800 text-white text-xs py-1.5 rounded disabled:opacity-50"
      >
        {disabled ? 'Salvando…' : 'Salvar'}
      </button>
    </div>
  )
}

// ─── Helpers de UI ───────────────────────────────────────────────────────────
function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-wide text-slate-500">{label}</p>
      <p className="text-sm text-slate-800">{value}</p>
    </div>
  )
}

function Input({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-wide text-slate-500 mb-1">{label}</label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
      />
    </div>
  )
}

function NumberInput({
  label,
  value,
  onChange,
}: {
  label: string
  value: number | null
  onChange: (v: number | null) => void
}) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-wide text-slate-500 mb-1">{label}</label>
      <input
        type="number"
        step="0.01"
        value={value ?? ''}
        onChange={(e) => {
          const n = e.target.value === '' ? null : Number(e.target.value)
          onChange(Number.isFinite(n as number) ? (n as number) : null)
        }}
        className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
      />
    </div>
  )
}

function Textarea({
  label,
  value,
  onChange,
  rows = 4,
  placeholder,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  rows?: number
  placeholder?: string
}) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-wide text-slate-500 mb-1">{label}</label>
      <textarea
        rows={rows}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm font-mono"
      />
    </div>
  )
}

type ProdutoDetalheData = Awaited<ReturnType<typeof buscarProduto>>
